/**
 * WP 8.7 — the only file that talks to `grocery_aisles`, the same "own new
 * file rather than `db.ts`/`queries.ts`" convention `seen.ts` (8.4) and
 * `tickets.ts` (8.6) both follow (`docs/asks-plan/README.md`, "Shared
 * files").
 *
 * `useGroceryAisles()` reads the shared vocabulary (`grocery_aisles`: what
 * the Worker learned, `source: 'model'`, and what an admin corrected,
 * `source: 'admin'`) and the signed-in person's own overrides
 * (`grocery_aisle_overrides`, U4.1) and folds them into the `known`
 * dictionary `aisleOf`/`needsLookup` (`aisles.ts`) take as their second
 * argument. The person's own row wins for the same term, decided once here
 * (`mergeKnown`) rather than inside the pure matcher, which only ever sees
 * one dictionary and does not know where a row came from.
 *
 * `useSetAisleOverride` is "Move to…": it always writes the person's own
 * override, and for an admin also the shared row (`aisleWriteTargets`) —
 * the shared table's insert/update is `is_admin()` only (U0.2), so a
 * non-admin never attempts that write. `requestAisleLookup` is the fire-and-forget
 * call to the Worker's `/aisle` on a genuine miss; it writes nothing itself
 * (only the Worker, with its service key, may write `source: 'model'`) and
 * just invalidates the query on success so the next read brings the
 * learned row.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from './auth'
import { supabase } from './supabase'
import { fetchAll } from './fetchAll'
import { useEffect, useMemo, useState } from 'react'
import { aisleDictNow, loadAisleDict, normalise, mergeKnown, aisleWriteTargets, type Dict } from './aisles'
import { useIsAdmin } from './useIsAdmin'

const WORKER_URL = (import.meta.env.VITE_WORKER_URL as string | undefined)?.replace(/\/+$/, '')

export interface GroceryAisleRow {
  term: string
  aisle: string
  source: 'model' | 'admin'
}

const GROCERY_AISLES = ['grocery_aisles'] as const
const GROCERY_AISLE_OVERRIDES = ['grocery_aisle_overrides'] as const

async function fetchGroceryAisles(): Promise<GroceryAisleRow[]> {
  const { data, error } = await fetchAll<GroceryAisleRow>(() =>
    supabase.from('grocery_aisles').select('term,aisle,source').order('term'))
  if (error) throw new Error(error.message)
  return data
}

const toDict = (rows: ReadonlyArray<{ term: string; aisle: string }>): Dict =>
  rows.map(r => [r.term, r.aisle] as const)

async function fetchAisleOverrides(): Promise<{ term: string; aisle: string }[]> {
  // RLS already narrows this to the caller's own rows; no user filter needed.
  const { data, error } = await fetchAll<{ term: string; aisle: string }>(() =>
    supabase.from('grocery_aisle_overrides').select('term,aisle').order('term'))
  if (error) throw new Error(error.message)
  return data
}

/** The learned + overridden dictionary, ready to pass as `aisleOf`'s and
 *  `needsLookup`'s second argument: the person's own corrections first, the
 *  shared rows after (`mergeKnown`). `data` stays undefined until either
 *  table has answered, as the single query it replaces did, so callers'
 *  `?? []` still reads the same. */
export function useGroceryAisles(): { data: Dict | undefined } {
  const { session } = useAuth()
  const shared = useQuery({
    queryKey: GROCERY_AISLES,
    queryFn: fetchGroceryAisles,
    enabled: !!session,
    select: toDict,
  })
  const mine = useQuery({
    queryKey: GROCERY_AISLE_OVERRIDES,
    queryFn: fetchAisleOverrides,
    enabled: !!session,
    select: toDict,
  })
  const data = useMemo(
    () => (shared.data || mine.data) ? mergeKnown(shared.data ?? [], mine.data ?? []) : undefined,
    [shared.data, mine.data],
  )
  return { data }
}

/**
 * The built dictionary (`aisles.ts`), loaded as its own chunk the first
 * time a grocery list mounts and `null` until it arrives. The list passes
 * it to `aisleOf` and `needsLookup` explicitly rather than letting them
 * read the module-level copy, so "the dictionary has not loaded" and "the
 * dictionary has nothing on this term" stay two different facts — the
 * second asks the Worker, the first must not.
 */
export function useAisleDict(): Dict | null {
  const [dict, setDict] = useState<Dict | null>(() => (aisleDictNow().length ? aisleDictNow() : null))
  useEffect(() => {
    if (dict) return
    let on = true
    void loadAisleDict().then(d => { if (on) setDict(d) }).catch(() => { /* offline on a cold start: Other until the next mount */ })
    return () => { on = false }
  }, [dict])
  return dict
}

/**
 * "Move to…". The person's own override is written first and always; an
 * admin's correction *also* writes the shared row, so what Doug fixes
 * becomes everyone's default. One action, two writes, both intended — and
 * not the one-value-two-jobs bug: the override is the user's record of what
 * they decided, the shared row is the vocabulary other people start from.
 * If the shared write fails after the override landed, the admin's own list
 * is already right and the error still surfaces.
 */
export function useSetAisleOverride() {
  const qc = useQueryClient()
  const isAdmin = useIsAdmin()
  return useMutation({
    mutationFn: async (vars: { text: string; aisle: string }) => {
      const term = normalise(vars.text)
      for (const table of aisleWriteTargets(isAdmin)) {
        const res = table === 'override'
          // `user_id` defaults to auth.uid() and RLS pins it to that.
          ? await supabase.from('grocery_aisle_overrides')
              .upsert({ term, aisle: vars.aisle, at: new Date().toISOString() }, { onConflict: 'user_id,term' })
          : await supabase.from('grocery_aisles')
              .upsert({ term, aisle: vars.aisle, source: 'admin' }, { onConflict: 'term' })
        if (res.error) throw new Error(res.error.message)
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: GROCERY_AISLE_OVERRIDES })
      void qc.invalidateQueries({ queryKey: GROCERY_AISLES })
    },
  })
}

/**
 * Fire-and-forget: `GroceryList.tsx` calls this once, on add, for a term
 * that landed on `Other` with no fuzzy hit anywhere (`needsLookup`) — never
 * on render, never twice for the same term in one session (the caller's own
 * debounce). Writes nothing itself; the Worker writes the learned row with
 * its service key, and this only refreshes `useGroceryAisles()`'s query so
 * the item moves once that row exists. Swallows every failure — an
 * unreachable Worker must never block or undo the add.
 */
export async function requestAisleLookup(text: string, qc: ReturnType<typeof useQueryClient>): Promise<void> {
  if (!WORKER_URL) return
  try {
    const { data: { session } } = await supabase.auth.getSession()
    const token = session?.access_token
    if (!token) return
    const res = await fetch(`${WORKER_URL}/aisle?q=${encodeURIComponent(text)}`, {
      headers: { authorization: `Bearer ${token}` },
    })
    if (!res.ok) return
    void qc.invalidateQueries({ queryKey: GROCERY_AISLES })
  } catch {
    // Unreachable Worker, offline, whatever — the brief's own rule: the item
    // stays in Other, nothing blocks the add.
  }
}
